import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Download, LogOut, RefreshCw, Users, UserCheck, ThumbsUp, FlaskConical, ShoppingCart, Boxes, TrendingUp, MailQuestion } from "lucide-react";
import { LogoMark, LogoWord } from "../components/Logo";
import Seo from "../components/Seo";
import PriceAnalysis from "../components/admin/PriceAnalysis";
import { API, api } from "../lib/api";
import { METIERS, REGIONS } from "../lib/constants";

const CHART_COLORS = ["#FFC700", "#F9FAFB", "#9CA3AF", "#F59E0B", "#10B981", "#6B7280"];
const tooltipStyle = { background: "#141619", border: "1px solid #2E333D", borderRadius: 8, color: "#F9FAFB", fontSize: 12 };

const Card = ({ title, children, className = "", testid }) => (
  <div className={`border border-[#2E333D] bg-[#141619] rounded-xl p-5 ${className}`} data-testid={testid}>
    <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-4">{title}</h3>
    {children}
  </div>
);

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-[#2E333D] bg-[#141619] rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</span>
      <Icon size={17} className="text-[#FFC700]" />
    </div>
    <p className="mt-3 font-display text-4xl font-extrabold text-[#F9FAFB]">{value}</p>
  </div>
);

const FILTER_DEFS = [
  { key: "metier", label: "Métier", options: METIERS },
  { key: "region", label: "Région", options: REGIONS },
  { key: "prototype", label: "Prototype", options: ["oui", "peut_etre", "non"] },
  { key: "intention", label: "Intention d'achat", options: ["oui", "non"] },
  { key: "intention_lancement", label: "Suivi lancement", options: ["oui", "peut_etre", "non"] },
  { key: "lead_type", label: "Type", options: ["questionnaire", "notification", "prix"] },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [priceStats, setPriceStats] = useState(null);
  const [leads, setLeads] = useState({ total: 0, items: [] });
  const [filters, setFilters] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const queryString = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p.toString();
  }, [filters, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, l] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/price-stats"),
        api.get(`/api/admin/leads?limit=200&${queryString()}`),
      ]);
      setStats(s.data);
      setPriceStats(p.data);
      setLeads(l.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/admin/login");
      } else {
        toast.error("Erreur de chargement des statistiques");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, queryString]);

  useEffect(() => {
    api.get("/api/auth/me").then(() => load()).catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/api/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const selectCls = "rounded-lg border border-[#2E333D] bg-[#0C0D0E] px-3 py-2 text-xs text-gray-300 focus:border-[#FFC700] focus:outline-none";

  return (
    <div className="bg-[#0C0D0E] min-h-screen" data-testid="admin-dashboard">
      <Seo title="Dashboard — STOCKBOX 8" noindex />
      <header className="border-b border-[#2E333D] bg-[#0C0D0E]/90 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LogoMark size={28} />
            <LogoWord />
            <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-[#FFC700] border border-[#FFC700]/40 rounded-full px-2.5 py-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="p-2.5 rounded-lg border border-[#2E333D] text-gray-400 hover:text-[#FFC700] transition-colors" aria-label="Actualiser">
              <RefreshCw size={16} />
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="p-2.5 rounded-lg border border-[#2E333D] text-gray-400 hover:text-red-400 transition-colors" aria-label="Déconnexion">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {loading && !stats ? (
          <p className="text-gray-500 font-mono text-sm" data-testid="admin-loading">Chargement des statistiques...</p>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Users} label="Visiteurs" value={stats.visitors} testid="stat-visitors" />
              <Stat icon={UserCheck} label="Inscrits" value={stats.leads} testid="stat-leads" />
              <Stat icon={MailQuestion} label="Réponses étude prix" value={priceStats?.total ?? 0} testid="stat-price-responses" />
              <Stat icon={FlaskConical} label="Testeurs" value={stats.testers} testid="stat-testers" />
              <Stat icon={ShoppingCart} label="Intentions d'achat" value={stats.purchase_intent} testid="stat-purchase-intent" />
              <Stat icon={Boxes} label="Boîtes potentielles" value={stats.potential_boxes} testid="stat-potential-boxes" />
              <Stat icon={TrendingUp} label="Taux de conversion" value={`${stats.conversion_rate} %`} testid="stat-conversion" />
              <Stat icon={ThumbsUp} label="Intéressés (prix)" value={stats.interested} testid="stat-interested" />
            </div>

            <PriceAnalysis stats={priceStats} />

            <div className="grid lg:grid-cols-2 gap-4">
              <Card title="Inscriptions dans le temps" testid="chart-leads-by-day">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={stats.leads_by_day}>
                    <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 10 }} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="count" stroke="#FFC700" fill="#FFC700" fillOpacity={0.15} name="Inscrits" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Intérêt par métier" testid="chart-by-metier">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.by_metier.slice(0, 9)}>
                    <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 10 }} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="#FFC700" radius={[4, 4, 0, 0]} name="Inscrits" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Intérêt par région" testid="chart-by-region">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.by_region.slice(0, 8)}>
                    <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 10 }} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="#F9FAFB" radius={[4, 4, 0, 0]} name="Inscrits" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Nombre de boîtes envisagées" testid="chart-by-nbbox">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={stats.by_nb_box} dataKey="count" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={3}>
                      {stats.by_nb_box.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Demandes de prototype" testid="chart-by-prototype">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={stats.by_prototype} dataKey="count" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={3}>
                      {stats.by_prototype.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Source des visiteurs (utm_source)" testid="chart-by-source">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.by_source.slice(0, 8)}>
                    <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 10 }} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="#FFC700" radius={[4, 4, 0, 0]} name="Inscrits" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Performance des campagnes (utm_campaign)" testid="chart-by-campaign">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.by_campaign.slice(0, 8)}>
                    <CartesianGrid stroke="#2E333D" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6B7280", fontSize: 10 }} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="#F9FAFB" radius={[4, 4, 0, 0]} name="Inscrits" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            <Card title={`Prospects (${leads.total})`} testid="leads-panel">
              <div className="flex flex-wrap gap-2 mb-5">
                {FILTER_DEFS.map((f) => (
                  <select key={f.key} value={filters[f.key] || ""} data-testid={`filter-${f.key}`}
                    onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                    className={selectCls} aria-label={f.label}>
                    <option value="">{f.label} : tous</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ))}
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={selectCls} data-testid="filter-date-from" aria-label="Date de début" />
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={selectCls} data-testid="filter-date-to" aria-label="Date de fin" />
                <button onClick={load} data-testid="filter-apply-button"
                  className="rounded-lg bg-[#FFC700] text-[#0C0D0E] font-semibold text-xs px-4 py-2 hover:bg-[#FFD83D] transition-colors">
                  Appliquer
                </button>
                <button onClick={() => { setFilters({}); setDateFrom(""); setDateTo(""); setTimeout(load, 0); }} data-testid="filter-reset-button"
                  className="rounded-lg border border-[#2E333D] text-gray-400 text-xs px-4 py-2 hover:border-gray-500 transition-colors">
                  Réinitialiser
                </button>
                <a href={`${API}/api/admin/export.csv?${queryString()}`} data-testid="export-csv-button"
                  className="ml-auto inline-flex items-center gap-2 rounded-lg border border-[#FFC700]/50 text-[#FFC700] font-semibold text-xs px-4 py-2 hover:bg-[#FFC700]/10 transition-colors">
                  <Download size={14} /> Export CSV
                </a>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500 border-b border-[#2E333D]">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Prénom</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Métier</th>
                      <th className="py-2 pr-3">Région</th>
                      <th className="py-2 pr-3">Boîtes</th>
                      <th className="py-2 pr-3">Proto</th>
                      <th className="py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.items.length === 0 && (
                      <tr><td colSpan={9} className="py-8 text-center text-gray-600" data-testid="leads-empty">Aucun prospect pour le moment. Les compteurs démarrent à zéro.</td></tr>
                    )}
                    {leads.items.map((l) => (
                      <tr key={l.id} className="border-b border-[#2E333D]/50 text-gray-300" data-testid={`lead-row-${l.id}`}>
                        <td className="py-2.5 pr-3 font-mono text-gray-500">{(l.created_at || "").slice(0, 10)}</td>
                        <td className="py-2.5 pr-3">{l.lead_type || "—"}</td>
                        <td className="py-2.5 pr-3">{l.prenom || "—"}</td>
                        <td className="py-2.5 pr-3">{l.email || "—"}</td>
                        <td className="py-2.5 pr-3">{l.metier || "—"}</td>
                        <td className="py-2.5 pr-3">{l.region || "—"}</td>
                        <td className="py-2.5 pr-3">{l.nb_box || "—"}</td>
                        <td className="py-2.5 pr-3">{l.prototype || "—"}</td>
                        <td className="py-2.5">{l.utm_source || l.source || "direct"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ) : null}
      </main>
    </div>
  );
}
